import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, Optional, Self, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { SharedMaterialUIModule } from '../../modules/shared-material-ui.module';

export type SearchableLookupOption = {
  id: string;
  name: string | null;
  code: string | null;
};

// A dropdown with a search box, for a long list that is loaded as it is searched. Use it as `<searchable-lookup-select formControlName="x" ... />`.
//
// The select inside is bound to the same control as the one outside (as in lookup-select), so Material works out the value, disabled, the
// required marker and the red error state itself. Before, the value and the disabled state were kept in plain fields: in an app without
// zone.js a value written by code (a saved record being loaded) could stay unseen until something else redrew the page, and the select
// never showed an error because it was bound to nothing. This component only draws the field, so it is not a value accessor itself (the
// methods below do nothing), but it must say so, or the form would look for one.
@Component({
  selector: 'searchable-lookup-select',
  imports: [ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './searchable-lookup-select.component.html',
  styles: `
    .search-input {
      width: 100%;
      border: none;
      outline: none;
      background: transparent;
      color: var(--mat-sys-on-surface);
      font: var(--mat-sys-body-large);
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SearchableLookupSelectComponent implements ControlValueAccessor, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly label = input('Select option');
  readonly placeholder = input('Select option');
  readonly searchPlaceholder = input('Search');
  readonly loadingLabel = input('Loading...');
  readonly requiredError = input('This field is required.');
  readonly showCode = input(false);
  readonly options = input<SearchableLookupOption[]>([]);
  readonly loading = input(false);
  // Filter the options here, by name and code. Leave off when the parent searches the server through (searchChanged).
  readonly filterLocally = input(false);

  readonly searchChanged = output<string>();
  readonly selectionChanged = output<string>();

  protected readonly search = signal('');

  // Bumped whenever the control is touched, changes value or changes status. A form marked as touched by code (a step that is not
  // complete) would not otherwise redraw this view, and the required error would not show.
  private readonly controlState = signal(0);

  // The option last chosen, so it stays in the list when a search leaves it out: a select whose chosen option is not drawn shows nothing.
  private readonly selectedOption = signal<SearchableLookupOption | null>(null);

  protected readonly visibleOptions = computed(() => {
    this.controlState();
    const term = this.search().trim().toLowerCase();
    let options = this.options();

    if (this.filterLocally() && term) {
      options = options.filter(
        (option) => (option.name ?? '').toLowerCase().includes(term) || (option.code ?? '').toLowerCase().includes(term),
      );
    }

    const value = this.control.value;
    const remembered = this.selectedOption();
    const selected = remembered?.id === value ? remembered : this.options().find((option) => option.id === value);
    if (selected && !options.some((option) => option.id === selected.id)) {
      options = [selected, ...options];
    }

    return options;
  });

  private readonly fallbackControl = new FormControl('');

  constructor(@Optional() @Self() protected readonly ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // The control the field is bound to (a free-standing one when the component is used without a form).
  protected get control(): FormControl {
    return (this.ngControl?.control as FormControl | null | undefined) ?? this.fallbackControl;
  }

  ngAfterViewInit(): void {
    this.control.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.controlState.update((count) => count + 1));
  }

  // Value accessor members: nothing to do, the select inside is bound to the control directly.
  writeValue(): void {}
  registerOnChange(): void {}
  registerOnTouched(): void {}

  protected onSelectionChanged(value: string): void {
    this.selectedOption.set(this.options().find((option) => option.id === value) ?? null);
    this.selectionChanged.emit(value);
  }

  // Put the cursor in the search box when the list opens, and clear the search when it closes so the next open starts with the full list.
  protected onOpenedChange(open: boolean, searchInput: HTMLInputElement): void {
    if (open) {
      searchInput.focus();
      return;
    }

    if (this.search()) {
      this.onSearchChanged('');
    }
  }

  protected onSearchChanged(value: string): void {
    this.search.set(value);
    this.searchChanged.emit(value.trim());
  }

  protected hasRequiredError(): boolean {
    this.controlState();
    return this.control.touched && this.control.hasError('required');
  }

  protected optionLabel(option: SearchableLookupOption): string {
    const name = option.name ?? option.code ?? 'Unnamed option';
    if (!this.showCode() || !option.code) {
      return name;
    }

    return `${name} (${option.code})`;
  }
}
